import * as React from 'react';
import { DraggableData, DraggableEvent, DraggableProps } from 'react-draggable';

import {
  CardBody,
  CardHeader,
  DraggableIconContainer,
  HeaderContentContainer,
  HeaderSpacer,
  StyledCard,
  StyledCloseIcon,
  StyledDraggable,
  StyledDraggableIcon,
} from './DraggableCard.styles';
import { useStoreCardPosition } from './hooks';

/**
 * A config object that helps drive the card position "memory" feature.
 * NOTE: The user identifier is injected to keep coupling between Application context and the Component low.
 */
export interface StoreCardPositionConfig {
  userIdentifier: string;
  storedCardPositionKey?: string;
}

export type DraggableCardDefaultPosition = Partial<Pick<DraggableProps, 'defaultPosition'>>;

export interface DraggableCardProps extends DraggableCardDefaultPosition {
  isOpen: boolean;
  headerContent?: React.ReactNode;
  storeCardPositionConfig?: StoreCardPositionConfig;
  onClose?: () => void;
}

const DRAGGABLE_HANDLE_ID = 'draggable-popover-handle';
const STORED_CARD_POSITION_KEY = 'draggable-card'; // NOTE: This is not instance specific.

const DraggableCard = ({
  isOpen,
  headerContent,
  onClose,
  children,
  defaultPosition,
  storeCardPositionConfig,
  ...rest
}: React.PropsWithChildren<DraggableCardProps>) => {
  // Dependencies
  const { storeCardPosition, getCardPosition } = useStoreCardPosition(
    storeCardPositionConfig?.storedCardPositionKey || STORED_CARD_POSITION_KEY,
  );

  // State
  const [isDragging, setIsDragging] = React.useState(false);

  // Handlers
  const handleClickClose = () => {
    setIsDragging(false);
    onClose?.();
  };

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragStop = (_: DraggableEvent, data: DraggableData) => {
    setIsDragging(false);

    if (!storeCardPositionConfig) {
      return;
    }

    const nextCardPosition = { x: data.x, y: data.y };
    storeCardPosition(storeCardPositionConfig.userIdentifier, nextCardPosition);
  };

  // Derived State
  const storedCardPosition = storeCardPositionConfig?.userIdentifier
    ? getCardPosition(storeCardPositionConfig.userIdentifier)
    : undefined;
  const storeCardPositionDraggableProps: DraggableCardDefaultPosition = {
    defaultPosition: storedCardPosition ?? defaultPosition,
  };

  return isOpen ? (
    <StyledDraggable
      handle={`#${DRAGGABLE_HANDLE_ID}`}
      onStart={handleDragStart}
      onStop={handleDragStop}
      {...storeCardPositionDraggableProps}
    >
      <StyledCard $isDragging={isDragging} {...rest}>
        <CardHeader id={DRAGGABLE_HANDLE_ID}>
          <DraggableIconContainer>
            <StyledDraggableIcon />
          </DraggableIconContainer>
          <HeaderContentContainer>{headerContent}</HeaderContentContainer>
          <HeaderSpacer />
          <StyledCloseIcon onClick={handleClickClose} />
        </CardHeader>
        <CardBody>{children}</CardBody>
      </StyledCard>
    </StyledDraggable>
  ) : null;
};

export default DraggableCard;
